import { sanitizeTrackingSourceUrl, trackingQueryParameters } from '@vendepay/contracts';
export interface CheckoutUtmAttribution {
  readonly source?: string;
  readonly medium?: string;
  readonly campaign?: string;
  readonly content?: string;
  readonly term?: string;
  readonly sck?: string;
  readonly src?: string;
}

export interface CheckoutAttribution {
  readonly affiliateRef?: string;
  readonly utm?: CheckoutUtmAttribution;
  readonly trackingIdentifiers?: CheckoutTrackingIdentifiers;
  readonly recuperaaiCartId?: string;
}

export interface CheckoutTrackingIdentifiers {
  readonly sourceUrl?: string;
  readonly fbclid?: string;
  readonly ttclid?: string;
  readonly gclid?: string;
  readonly fbp?: string;
  readonly fbc?: string;
}

const STORAGE_PREFIX = 'vendepay:checkout-attribution:';

const UTM_PARAMS = [
  ['utm_source', 'source'],
  ['utm_medium', 'medium'],
  ['utm_campaign', 'campaign'],
  ['utm_content', 'content'],
  ['utm_term', 'term'],
  ['sck', 'sck'],
  ['src', 'src'],
] as const;

export function captureCheckoutAttribution(checkoutId: string, searchParams: URLSearchParams, pageUrl?: string): CheckoutAttribution {
  const stored = readStoredAttribution(checkoutId);
  const affiliateRef = clean(searchParams.get('ref')) ?? clean(searchParams.get('affiliate'));
  const recuperaaiCartId = clean(searchParams.get('recuperaai_cart_id')) ?? stored.recuperaaiCartId;
  const utm = utmFromSearch(searchParams);
  const capturedIdentifiers = trackingIdentifiersFromSearch(searchParams);
  const sourceUrl = sanitizeTrackingSourceUrl(pageUrl);
  const identifiers = { ...stored.trackingIdentifiers, ...capturedIdentifiers };
  if (sourceUrl !== undefined) {
    const source = new URL(sourceUrl);
    for (const [key, value] of Object.entries(trackingQueryParameters(utm ?? stored.utm, identifiers))) {
      source.searchParams.set(key, value);
    }
    const enrichedSourceUrl = sanitizeTrackingSourceUrl(source.toString());
    if (enrichedSourceUrl !== undefined) identifiers.sourceUrl = enrichedSourceUrl;
    else delete identifiers.sourceUrl;
  }
  const trackingIdentifiers = Object.keys(identifiers).length ? identifiers : undefined;
  const next: CheckoutAttribution = {
    ...(stored.utm !== undefined ? { utm: stored.utm } : {}),
    ...(stored.trackingIdentifiers !== undefined ? { trackingIdentifiers: stored.trackingIdentifiers } : {}),
    ...(affiliateRef !== undefined ? { affiliateRef } : {}),
    ...(utm !== undefined ? { utm } : {}),
    ...(trackingIdentifiers !== undefined ? { trackingIdentifiers } : {}),
    ...(recuperaaiCartId !== undefined ? { recuperaaiCartId } : {}),
  };

  if (
    affiliateRef !== undefined ||
    utm !== undefined ||
    trackingIdentifiers !== undefined ||
    recuperaaiCartId !== undefined ||
    stored.affiliateRef !== undefined
  ) {
    writeStoredAttribution(checkoutId, next);
  }

  return next;
}

function readStoredAttribution(checkoutId: string): CheckoutAttribution {
  try {
    const raw = window.localStorage.getItem(storageKey(checkoutId));
    if (raw === null) {
      return {};
    }
    return sanitizeAttribution(JSON.parse(raw));
  } catch {
    return {};
  }
}

function writeStoredAttribution(checkoutId: string, attribution: CheckoutAttribution): void {
  try {
    window.localStorage.setItem(storageKey(checkoutId), JSON.stringify(attribution));
  } catch {
  }
}

function sanitizeAttribution(value: unknown): CheckoutAttribution {
  if (!isObject(value)) {
    return {};
  }
  const affiliateRef = clean(value.affiliateRef);
  const utm = sanitizeUtm(value.utm);
  const trackingIdentifiers = sanitizeTrackingIdentifiers(value.trackingIdentifiers);
  const recuperaaiCartId = clean(value.recuperaaiCartId);
  return {
    ...(affiliateRef !== undefined ? { affiliateRef } : {}),
    ...(utm !== undefined ? { utm } : {}),
    ...(trackingIdentifiers !== undefined ? { trackingIdentifiers } : {}),
    ...(recuperaaiCartId !== undefined ? { recuperaaiCartId } : {}),
  };
}

function sanitizeUtm(value: unknown): CheckoutUtmAttribution | undefined {
  if (!isObject(value)) {
    return undefined;
  }
  const candidate = Object.fromEntries(
    UTM_PARAMS.flatMap(([, fieldKey]) => {
      const cleaned = clean(value[fieldKey]);
      return cleaned === undefined ? [] : [[fieldKey, cleaned]];
    }),
  ) as CheckoutUtmAttribution;
  return Object.keys(candidate).length > 0 ? candidate : undefined;
}

export function utmFromSearch(searchParams: URLSearchParams): CheckoutUtmAttribution | undefined {
  const candidate = Object.fromEntries(
    UTM_PARAMS.flatMap(([queryKey, fieldKey]) => {
      const value = clean(searchParams.get(queryKey));
      return value === undefined ? [] : [[fieldKey, value]];
    }),
  ) as CheckoutUtmAttribution;
  return Object.keys(candidate).length > 0 ? candidate : undefined;
}

function trackingIdentifiersFromSearch(searchParams: URLSearchParams): CheckoutTrackingIdentifiers | undefined {
  const fbclid = clean(searchParams.get('fbclid'));
  const ttclid = clean(searchParams.get('ttclid'));
  const gclid = clean(searchParams.get('gclid'));
  const fbp = clean(readCookie('_fbp'));
  const fbcFromCookie = clean(readCookie('_fbc'));
  const fbc = fbcFromCookie ?? (fbclid !== undefined ? `fb.1.${Date.now()}.${fbclid}` : undefined);
  if (fbcFromCookie === undefined && fbc !== undefined) {
    writeCookie('_fbc', fbc);
  }
  const candidate: CheckoutTrackingIdentifiers = {
    ...(fbclid !== undefined ? { fbclid } : {}),
    ...(ttclid !== undefined ? { ttclid } : {}),
    ...(gclid !== undefined ? { gclid } : {}),
    ...(fbp !== undefined ? { fbp } : {}),
    ...(fbc !== undefined ? { fbc } : {}),
  };
  return Object.keys(candidate).length > 0 ? candidate : undefined;
}

function sanitizeTrackingIdentifiers(value: unknown): CheckoutTrackingIdentifiers | undefined {
  if (!isObject(value)) {
    return undefined;
  }
  const sourceUrl = sanitizeTrackingSourceUrl(typeof value.sourceUrl === 'string' ? value.sourceUrl : undefined);
  const fbclid = clean(value.fbclid);
  const ttclid = clean(value.ttclid);
  const gclid = clean(value.gclid);
  const fbp = clean(value.fbp);
  const fbc = clean(value.fbc);
  const candidate: CheckoutTrackingIdentifiers = {
    ...(sourceUrl !== undefined ? { sourceUrl } : {}),
    ...(fbclid !== undefined ? { fbclid } : {}),
    ...(ttclid !== undefined ? { ttclid } : {}),
    ...(gclid !== undefined ? { gclid } : {}),
    ...(fbp !== undefined ? { fbp } : {}),
    ...(fbc !== undefined ? { fbc } : {}),
  };
  return Object.keys(candidate).length > 0 ? candidate : undefined;
}

function storageKey(checkoutId: string): string {
  return `${STORAGE_PREFIX}${checkoutId}`;
}

function clean(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readCookie(name: string): string | undefined {
  const prefix = `${encodeURIComponent(name)}=`;
  const cookie = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));
  return cookie === undefined ? undefined : decodeURIComponent(cookie.slice(prefix.length));
}

function writeCookie(name: string, value: string): void {
  const expires = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}
